import { z } from 'zod';

// Native <select> writes strings into form state ('' when the placeholder is selected, '42' when an
// option is picked). Normalize '' / null / undefined → null, otherwise the numeric id.
// `.optional()` is required: fields not rendered for the current competition kind (team_id / is_reserve
// in regular comps, the category selects in team comps) are never registered, so their keys are absent
// from the submitted values — and zod 4 rejects an absent key unless the schema is explicitly optional.
const optionalIdFromSelect = z
  .unknown()
  .optional()
  .transform((v): number | null => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  });

// Native <select> writes 'true' / 'false' strings; edit-mode defaults arrive as a real boolean.
const booleanFromSelect = z
  .unknown()
  .optional()
  .transform((v): boolean => v === true || v === 'true');

const baseSchema = z.object({
  competition_type_id: optionalIdFromSelect,
  weight_class_id: optionalIdFromSelect,
  category_id: optionalIdFromSelect,
  person_id: z.number(),
  team_id: optionalIdFromSelect,
  is_reserve: booleanFromSelect,
});

/**
 * Validation differs by competition kind:
 * - Team competitions: a lifter is registered by team alone (no registration category), so the team
 *   is the only required selector.
 * - Regular competitions: the registration category (tävlingstyp + ålderskategori) stays required,
 *   and there is no team to pick.
 */
export function makeRegisterLifterSchema(isTeamComp: boolean) {
  return baseSchema.superRefine((value, ctx) => {
    if (isTeamComp) {
      if (value.team_id == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['team_id'], message: 'Välj lag' });
      }
      return;
    }

    if (value.competition_type_id == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['competition_type_id'], message: 'Välj tävlingstyp' });
    }
    if (value.category_id == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['category_id'], message: 'Välj kategori' });
    }
  });
}

export type Schema = z.infer<typeof baseSchema>;
export type SchemaInput = z.input<typeof baseSchema>;
